import _ from 'underscore';
import Promise from 'bluebird';
import { useState, useCallback, useContext, useMemo } from 'react';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import {
  loadCampaign,
  saveCampaign,
  loadBundleCountries,
  modelToViewModelForCampaign,
  readLocationCriterions,
  loadDisclaimerAssociations,
  readScheduleCriterions,
  readTargetCriterion,
  loadAssociatedPageFeeds,
} from '@bingads-webui-campaign-react/search-campaign-data';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import exclusionRepository from '@bingads-webui-campaign-legacy/advanced-settings/util/exclusion-repository';
import { ScopeLevel } from '@bingads-webui-campaign-react/constants';
import { entitySeparator, formatInvalidEntities } from '@bingads-webui-react/web-exclusions';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { showBudgetBidCampaignNotifications } from '@bingads-webui-campaign/import-shared-utils';
import { globalizationModelSetup } from '@bingads-webui-campaign/ads-globalization';

const defaultWebsites = [];

export const useCampaignEditSettingsRepository = () => {
  const i18n = useContext(I18nContext);
  const scenario = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);
  const serverContext = useContext(ServerContext);
  const instrumentation = useContext(InstrumentationContext);

  const { scope } = useContext(ScopeContext) || {};
  const {
    cid, aid, campaignId,
  } = scope.values;

  const {
    CurrentLocale: locale,
    DynamicPermissions,
    Permissions,
  } = serverContext;
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const { state, send } = useWizardService();
  const {
    websites: savedWebsites,
  } = state.context.wizardData;

  const [initialCampaign, setInitialCampaign] = useState({});

  const load = useCallback(() => {
    const loadPromises = {
      campaign: loadCampaign(cid, aid, campaignId),
      locationTargets: readLocationCriterions(cid, aid, campaignId),
      scheduleCriterion: readScheduleCriterions(cid, aid, campaignId),
      disclaimerAssociations: loadDisclaimerAssociations({ cid, aid, campaignId }),
      exclusions: exclusionRepository.load(campaignId, null, ScopeLevel.Campaign),
      targetCriterion: readTargetCriterion(cid, aid, campaignId),
      pageFeeds: loadAssociatedPageFeeds(cid, aid, campaignId),
    };

    return Promise.props(loadPromises)
      .then(async ({
        campaign: rawCampaign,
        adGroup,
        locationTargets,
        scheduleCriterion,
        disclaimerAssociations,
        exclusions: [campaignExclusions],
        targetCriterion,
        pageFeeds,
      }) => {
        const bundleCountriesAndLocationCriterions = await loadBundleCountries({
          cid, aid, serverContext, language: rawCampaign.Languages,
        });

        const campaign = modelToViewModelForCampaign({
          campaignModel: rawCampaign,
          locationCriterionsModel: locationTargets ? locationTargets.value : [],
          bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
          scheduleCriterionModel: scheduleCriterion ? scheduleCriterion.value : null,
          disclaimerAssociations: disclaimerAssociations ? disclaimerAssociations.value : [],
          targetCriterionModel: targetCriterion ? targetCriterion.value : null,
          pageFeeds: pageFeeds || [],
          isEdit: true,
        });

        if (permissions && permissions.IsDomesticSimplifiedChineseEnabled) {
          const { LANGUAGES: supportedLanguages } = globalizationModelSetup({ permissions, culture: locale });
          campaign.Languages = _.chain(campaign.Languages)
            .filter(language => _.contains(supportedLanguages, language))
            .value();
        }

        setInitialCampaign(campaign);

        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { exclusions: campaignExclusions });

        return Promise.resolve({
          campaign,
          adGroup,
          ...bundleCountriesAndLocationCriterions,
          websites: savedWebsites || defaultWebsites,
          exclusions: {
            campaignExclusions,
          },
        });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(`Load campaign settings failed: ${error}`);
        activity.signal(`onCampaignSettingsLoadFailed - ${JSON.stringify({
          campaignId,
          error,
        })}`);
        return Promise.reject(error);
      });
  }, [activity, aid, campaignId, cid, savedWebsites, send, serverContext, permissions, locale]);

  const save = useCallback(({ data }) => {
    const {
      campaign,
      bundleCountries,
      bundleLocationCriterions,
      websites,
      exclusions,
    } = data;

    const saveCampaignPromise = () => saveCampaign({
      campaign,
      bundleCountries,
      bundleLocationCriterions,
      websites,
      isEdit: true,
      permissions,
    }, {
      scenario: scenario.name,
      i18n,
      activityLogger: activity,
    });

    return saveCampaignPromise()
      .then(() => {
        const {
          exclusions: savedExclusions,
        } = state.context.wizardData;

        const { activity: BudgetBidCampaignImportNotificationActivity } = instrumentation.createScenarioAndActivity({
          scenarioName: 'BudgetBidCampaignImportNotification',
          activityName: 'SearchCampaignRevampSettingPage',
        });

        showBudgetBidCampaignNotifications({
          campaignItemList: [initialCampaign],
          oldBudget: initialCampaign.Budget,
          newBudget: campaign.Budget,
          oldBidStrategy: initialCampaign.BiddingScheme,
          newBidStrategy: campaign.BiddingScheme,
          activity: BudgetBidCampaignImportNotificationActivity,
        });

        const excludedIps = exclusions.campaignExclusions.ExcludedIPs.replace(entitySeparator, '');
        const excludedSites = exclusions.campaignExclusions.ExcludedSites.replace(entitySeparator, '');
        const isExclusionsChanged = !_.isEqual(excludedIps, savedExclusions.ExcludedIPs) || !_.isEqual(excludedSites, savedExclusions.ExcludedSites);
        if (isExclusionsChanged) {
          const saveExclusionsData = {
            campaignId: campaign.Id,
            AdvanceSettings: {
              Exclusions: {
                ExcludedIPs: excludedIps,
                ExcludedSites: excludedSites,
              },
            },
          };

          return exclusionRepository.saveExtensions(saveExclusionsData, ScopeLevel.Campaign);
        }

        return {};
      })
      .catch((error) => {
        const {
          serverErrors = [],
          systemErrors = [],
        } = error || {};
        let exclusionsAfterSave = exclusions;

        // exclusion error
        if (error.NegativeSitesResult || error.NegativeIPsResult) {
          serverErrors.push({
            property: 'webExclusions',
            NegativeSitesResult: error.NegativeSitesResult ? error.NegativeSitesResult.EntityErrors : undefined,
            NegativeIPsResult: error.NegativeIPsResult ? error.NegativeIPsResult.EntityErrors : undefined,
          });
          exclusionsAfterSave = {
            campaignExclusions: {
              ExcludedIPs: error.NegativeIPsResult ? formatInvalidEntities(error.NegativeIPsResult) : exclusions.campaignExclusions.ExcludedIPs,
              ExcludedSites: error.NegativeSitesResult ? formatInvalidEntities(error.NegativeSitesResult) : exclusions.campaignExclusions.ExcludedSites,
            },
          };
        }

        // eslint-disable-next-line no-console
        console.error(`Save campaign settings failed: ${error}`);
        activity.signal(`onCampaignSettingsSaveFailed - ${JSON.stringify({
          data,
          error,
        })}`);

        const newCampaign = error.campaign || campaign;
        // handle dsa settings when dsa settings save failed
        const isNonEditableDSAPropertyError = serverErrors.filter(serverError => _.contains(['DomainName'], serverError.property)).length > 0;
        if (!campaign.disableDsaSettings && isNonEditableDSAPropertyError) {
          newCampaign.disableDsaSettings = false;
        } else {
          newCampaign.disableDsaSettings = true;
        }

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          data: {
            campaign: newCampaign,
            exclusions: exclusionsAfterSave,
          },
          error: {
            serverErrors: _.flatten(serverErrors.filter(val => val !== null)),
            systemErrors: _.flatten(systemErrors.filter(val => val !== null)),
          },
        });
      });
  }, [activity, i18n, initialCampaign, instrumentation, permissions, scenario.name, state.context.wizardData]);

  return {
    load,
    save,
  };
};
